import { server } from "../api";
import axios from "axios";

/**
 * 重啟報告服務
 * @returns {Promise<Object>} 響應數據或錯誤信息
 */
const RestartReportService = async () => {
  try {
    const response = await axios.post(`${server}report/restart`);
    return response.data;
  } catch (err) {
    console.error("重啟報告服務失敗:", err);
    return err.response?.data || { code: 500, message: "重啟報告服務失敗" };
  }
};

/**
 * 獲取場域日報選項
 * @returns {Promise<Object>} 響應數據或錯誤信息
 */
const GetReportOption = async () => {
  try {
    const response = await axios.get(`${server}report/option`);
    return response.data;
  } catch (err) {
    console.error("獲取場域日報選項失敗:", err);
    return err.response?.data || { code: 500, message: "獲取場域日報選項失敗" };
  }
};

/**
 * 獲取場域日報數據
 * @returns {Promise<Object>} 響應數據或錯誤信息
 */
const GetReportData = async () => {
  try {
    const response = await axios.get(`${server}report/data`);
    return response.data;
  } catch (err) {
    console.error("獲取場域日報數據失敗:", err);
    return err.response?.data || { code: 500, message: "獲取場域日報數據失敗" };
  }
};

/**
 * 獲取影像機具日報選項
 * @returns {Promise<Object>} 響應數據或錯誤信息
 */
const GetImageReportOption = async () => {
  try {
    const response = await axios.get(`${server}report/image/option`);
    return response.data;
  } catch (err) {
    console.error("獲取影像機具日報選項失敗:", err);
    return err.response?.data || { code: 500, message: "獲取影像機具日報選項失敗" };
  }
};

/**
 * 獲取影像機具日報數據
 * @returns {Promise<Object>} 響應數據或錯誤信息
 */
const GetImageReportData = async () => {
  try {
    const response = await axios.get(`${server}report/image/data`);
    return response.data;
  } catch (err) {
    console.error("獲取影像機具日報數據失敗:", err);
    return err.response?.data || { code: 500, message: "獲取影像機具日報數據失敗" };
  }
};

/**
 * 更新場域日報
 * @param {Object} params 更新參數
 * @param {number} params.id 日報id
 * @param {number} params.field_id 場域ID
 * @param {string} params.time 通報時間
 * @param {string|null} params.serial_number 機具序號
 * @param {number} params.day_filter 排程選項 (0=全部,1=六日不發,2=日不發,3=見紅不發)
 * @returns {Promise<Object>} 響應數據或錯誤信息
 */
const UpdateReport = async ({ id, field_id, time, serial_number, day_filter=0 }) => {
  try {
    const response = await axios.post(`${server}report/update`, {
      report_id: id,
      field_id,
      time,
      serial_number,
      day_filter,
    });
    return response.data;
  } catch (err) {
    console.error("更新場域日報失敗:", err);
    return err.response?.data || { code: 500, message: "更新場域日報失敗" };
  }
};

/**
 * 更新影像機具日報
 * @param {Object} params 更新參數
 * @param {number} params.id 日報id
 * @param {number} params.field_mobile_id 機具id
 * @param {string} params.time 通報時間
 * @param {string} params.serial_number 機具序號
 * @param {boolean} params.is_sftp_download 是否存在beta下載
 * @param {number} params.day_filter 排程選項 (0=全部,1=六日不發,2=日不發,3=見紅不發)
 * @returns {Promise<Object>} 響應數據或錯誤信息
 */
const UpdateImageReport = async ({ 
  id, 
  field_mobile_id, 
  time, 
  serial_number, 
  is_sftp_download, 
  is_ftp_scan, 
  ftp_scan_time, 
  scan_time_gap, 
  scan_cd,
  day_filter = 0 
}) => {
  try {
    const response = await axios.post(`${server}report/image/update`, {
      report_id: id,
      field_mobile_id,
      time,
      serial_number,
      is_sftp_download: is_sftp_download ? 1 : 0, // 將布爾值轉換為 0 或 1
      is_ftp_scan: is_ftp_scan ? 1 : 0,
      ftp_scan_time,
      scan_time_gap: scan_time_gap ? Number(scan_time_gap) : null,
      scan_cd: scan_cd ? Number(scan_cd):null,
      day_filter,
    });
    return response.data;
  } catch (err) {
    console.error("更新影像機具日報失敗:", err);
    return err.response?.data || { code: 500, message: "更新影像機具日報失敗" };
  }
};

/**
 * 創建場域日報
 * @param {Object} params 創建參數
 * @param {number} params.field_id 場域ID
 * @param {string} params.time 通報時間
 * @param {string|null} params.serial_number 機具序號
 * @param {number} params.day_filter 排程選項 (0=全部,1=六日不發,2=日不發,3=見紅不發)
 * @returns {Promise<Object>} 響應數據或錯誤信息
 */
const CreateReport = async ({ field_id, time, serial_number, day_filter=0 }) => {
  try {
    const response = await axios.post(`${server}report/create`, {
      field_id,
      time,
      serial_number,
      day_filter,
    });
    return response.data;
  } catch (err) {
    console.error("創建場域日報失敗:", err);
    return err.response?.data || { code: 500, message: "創建場域日報失敗" };
  }
};

/**
 * 創建影像機具日報
 * @param {Object} params 創建參數
 * @param {number} params.field_mobile_id 機具id
 * @param {string} params.time 通報時間
 * @param {string} params.serial_number 機具序號
 * @param {boolean} params.is_sftp_download 是否存在beta下載
 * @param {number} params.day_filter 排程選項 (0=全部,1=六日不發,2=日不發,3=見紅不發)
 * @returns {Promise<Object>} 響應數據或錯誤信息
 */
const CreateImageReport = async ({ 
  field_mobile_id, 
  time, 
  serial_number, 
  is_sftp_download, 
  is_ftp_scan, 
  ftp_scan_time, 
  scan_time_gap, 
  scan_cd,
  day_filter = 0 
}) => {
  try {
    const response = await axios.post(`${server}report/image/create`, {
      field_mobile_id,
      time,
      serial_number,
      is_sftp_download: is_sftp_download ? 1 : 0, // 將布爾值轉換為 0 或 1
      is_ftp_scan: is_ftp_scan ? 1 : 0,
      ftp_scan_time,
      scan_time_gap: scan_time_gap ? Number(scan_time_gap) : null,
      scan_cd: scan_cd ? Number(scan_cd) : null,
      day_filter,
    });
    return response.data;
  } catch (err) {
    console.error("創建影像機具日報失敗:", err);
    return err.response?.data || { code: 500, message: "創建影像機具日報失敗" };
  }
};

/**
 * 刪除場域日報
 * @param {number} id 日報id
 * @returns {Promise<Object>} 響應數據或錯誤信息
 */
const DeleteReport = async (id) => {
  try {
    const response = await axios({
      method: "DELETE",
      url: `${server}report/delete`,
      data: { report_id: id },
    });
    return response.data;
  } catch (err) {
    console.error("刪除場域日報失敗:", err);
    return err.response?.data || { code: 500, message: "刪除場域日報失敗" };
  }
};

/**
 * 刪除影像機具日報
 * @param {number} id 日報id
 * @returns {Promise<Object>} 響應數據或錯誤信息
 */
const DeleteImageReport = async (id) => {
  try {
    const response = await axios({
      method: "DELETE",
      url: `${server}report/image/delete`,
      data: { report_id: id },
    });
    return response.data;
  } catch (err) {
    console.error("刪除影像機具日報失敗:", err);
    return err.response?.data || { code: 500, message: "刪除影像機具日報失敗" };
  }
};

export default {
  // 場域日報相關
  GetReportOption,
  GetReportData,
  UpdateReport,
  CreateReport,
  DeleteReport,

  // 影像機具日報相關
  GetImageReportOption,
  GetImageReportData,
  UpdateImageReport,
  CreateImageReport,
  DeleteImageReport,

  // 報告服務管理
  RestartReportService,
};
